/**
 * A row's own actions: the `⋯` menu, COPIED from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/support/inbox/action-dropdown.tsx (rizzui
 * `Dropdown`, a `⋯` `ActionIcon` trigger, icon + label items — the same source `row-actions.tsx`'s
 * own header cites), same shape `components/orders/order-state-menu.tsx` reuses for a row's own
 * direct mutation instead of a navigable item. Not `RowActions`: Resend and Revoke are mutations
 * this row commits itself, not routes to navigate to, and, unlike Cancel/Reject elsewhere in this
 * app, neither backend handler takes a reason, so there's no page for them to land on (Revoke confirms in-page via `useConfirm`). Its own
 * file, not `columns.tsx`, for the same reason `order-state-menu.tsx` is: the guard suite's check 9
 * caps how much styling markup a `columns.tsx` cell may carry regardless of its header comment.
 */
import { useState } from "react";
import { useRevalidator } from "react-router-dom";
import toast from "react-hot-toast";
import { PiDotsThreeVerticalBold, PiPaperPlaneTiltDuotone, PiProhibitDuotone } from "react-icons/pi";
import { ActionIcon, Dropdown } from "rizzui";
import { problemTitle } from "@steelyard/theme";
import { useConfirm } from "../../components/confirm-modal";
import { resendInvitation, revokeInvitation, type InvitationSummary } from "../../lib/services/invitations";

export function InvitationActions({ invitation }: { invitation: InvitationSummary }) {
  const revalidator = useRevalidator();
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const label = invitation.email ?? "this invitation";

  async function run(action: () => Promise<void>, done: string, fallback: string) {
    setBusy(true);
    try {
      await action();
      toast.success(`${label}: ${done}`);
      revalidator.revalidate();
    } catch (err) {
      toast.error(problemTitle(err, fallback));
    } finally {
      setBusy(false);
    }
  }

  function handleRevoke() {
    // RevokeInvitationHandler takes no reason, so this is a plain in-page confirmation.
    confirm({
      title: `Revoke the invitation to ${label}`,
      consequence: "This cannot be undone.",
      confirmLabel: "Revoke",
      onConfirm: () => void run(() => revokeInvitation(invitation.id), "revoked", "Could not revoke the invitation"),
    });
  }

  return (
    <Dropdown placement="bottom-start">
      <Dropdown.Trigger disabled={busy} aria-label={`Actions for ${label}`} title="Actions">
        <ActionIcon as="span" size="sm" variant="text" className="text-gray-600 hover:bg-gray-100 hover:text-gray-900">
          <PiDotsThreeVerticalBold className="size-4" />
        </ActionIcon>
      </Dropdown.Trigger>
      <Dropdown.Menu className="w-56">
        <Dropdown.Item
          disabled={busy}
          onClick={() => void run(() => resendInvitation(invitation.id), "resent", "Could not resend the invitation")}
          className="gap-2.5 text-sm"
        >
          <PiPaperPlaneTiltDuotone aria-hidden className="size-4 shrink-0" />
          Resend
        </Dropdown.Item>
        <Dropdown.Item disabled={busy} onClick={handleRevoke} className="mt-1 gap-2.5 border-t border-muted pt-2 text-sm text-red-dark">
          <PiProhibitDuotone aria-hidden className="size-4 shrink-0" />
          Revoke
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}
